import React, { FC, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import {
  RichTextEditor,
  Link,
  useRichTextEditorContext,
} from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import Highlight from "@tiptap/extension-highlight";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Superscript from "@tiptap/extension-superscript";
import SubScript from "@tiptap/extension-subscript";
import Image from "@tiptap/extension-image";
import { Collection } from "../../helpers/types";
import LoadingSpinner from "components/LoadingSpinner";
import BannerBreak from "components/BannerBreak";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Toast } from "flowbite-react";
import { BadgeCheckIcon, ExclamationCircleIcon } from "@heroicons/react/solid";
import { doGet, doPost } from "helpers/api_helper";
import FormItem from "components/FormItem";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import SelectTags from "components/SelectTags";
import ImageUpload from "components/ImageUpload";
import MediaUpload from "components/MediaUpload";
import { useParams } from "react-router-dom";
import GeomBackground from "components/Ui/BgGeom";
import { Tabs, TagsInput, Select, ComboboxItem } from '@mantine/core';

export interface PageCollectionProps {
  className?: string;
}

interface TagsObject {
  tags: string[];
}

const PageCollectionEdit: FC<PageCollectionProps> = ({ }) => {
  const [editorContent, setEditorContent] = useState<any>("");
  const [data, setData] = React.useState<Collection[]>([]);
  const [dataForm, setDataForm] = React.useState<Collection[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [details, setDetails] = React.useState<JSON>();
  const [sucMessage, setSucMessage] = useState<string>("");
  const [erMessage, setErMessage] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [bannerImage, setBannerImage] = useState<File | null>(null);
  const [avatarImage, setAvatarImage] = useState<File | null>(null);
  const [cardMedia, setCardMedia] = useState<File | null>(null);
  const [previewType, setPreviewType] = useState<"image" | "video" | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { path } = useParams<{ path: string }>();
  const [activeTab, setActiveTab] = useState<string | null>('details');
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [license, setLicense] = useState<string | null>('Undisclosed License');
  const [resetKey, setResetKey] = useState(0);

  // Reset errors after some time.
  React.useEffect(() => {
    if (sucMessage) {
      const timer = setTimeout(() => {
        setSucMessage("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [sucMessage]);

  React.useEffect(() => {
    if (erMessage) {
      const timer = setTimeout(() => {
        setErMessage("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [erMessage]);

  // Fetch all collection data from id
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await doGet<Collection>(
          `/api/collection/path/${path}`
        );
        setData([response.data]);
        setEditorContent(response.data.details);
        setSelectedTags(response.data.tags ?? []);
        setCustomTags(response.data.customTags ?? []);
        setLicense(response.data.license ?? 'Undisclosed License');
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [path]);

  // Seperate data states to stop editor re-render
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await doGet<Collection>(
          `/api/collection/path/${path}`
        );
        setDataForm([response.data]);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [path]);

  // Save content to DB as JSON
  const urlRegex = /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)+([\/][a-zA-Z0-9_\/]*)?$/;

  const onSave = useCallback(() => {
    if (
      (!urlRegex.test(dataForm[0]?.websitepath ?? "") &&
        dataForm[0]?.websitepath !== "") ||
      (!urlRegex.test(dataForm[0]?.twitterpath ?? "") &&
        dataForm[0]?.twitterpath !== "") ||
      (!urlRegex.test(dataForm[0]?.discordpath ?? "") &&
        dataForm[0]?.discordpath !== "")
    ) {
      setErMessage("Invalid URL format");
      return;
    }
    setLoading(true);
    doPost("/api/collection/edit/save", {
      details: editorContent,
      name: dataForm[0].name,
      id: data[0].id,
      description: dataForm[0].description,
      websitepath: dataForm[0].websitepath,
      twitterpath: dataForm[0].twitterpath,
      discordpath: dataForm[0].discordpath,
      artistpath: dataForm[0].artistpath,
      language: dataForm[0].language,
      library: dataForm[0].library,
      notes: dataForm[0].notes,
      credits: dataForm[0].credits,
      customTags: customTags,
      tags: selectedTags,
      license: license,
    }, {})
      .then(() => {
        setLoading(false);
        setSucMessage("Content Saved Successfully");
        console.log(dataForm[0].description)
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch((error) => {
        setLoading(false);
        setErMessage(`Error saving Content: ${error.message}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  }, [editorContent, path, data, dataForm, selectedTags, customTags, license]);

  // Create editor instance
  const editor = useEditor(
    {
      content: data[0]?.details,
      extensions: [
        StarterKit,
        Underline,
        Link,
        Superscript,
        SubScript,
        Image.configure({
          inline: true,
        }),
        Highlight,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
      ],
      onUpdate({ editor }) {
        const jsonContent: any = editor?.getJSON();
        setEditorContent(jsonContent);
      },
    },
    [data]
  );


  // Store editor content changes in state
  const handleEditorChange = React.useCallback((editor: any) => {
    const jsonContent: any = editor?.getJSON();
    setEditorContent(jsonContent);
  }, []);

  const addImage = useCallback(() => {
    const url = window.prompt("Paste Image URL:");

    if (url) {
      editor?.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  const handleTagsChange = (selectedValues: string[]) => {
    setSelectedTags(selectedValues);
    console.log(selectedValues);
  };

  const handleBannerImageSelect = (file: File) => {
    setBannerImage(file);
  };

  const handleAvatarImageSelect = (file: File) => {
    setAvatarImage(file);
  };

  const handleCardMediaSelect = (file: File) => {
    setCardMedia(file);
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (isImage) {
      setPreviewType("image");
    } else if (isVideo) {
      setPreviewType("video");
    } else {
      setPreviewType(null);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErMessage("");
    setSucMessage("");

    try {
      const formData = new FormData();
      if (bannerImage !== null) {
        formData.append("bannerImage", bannerImage);
      }

      if (avatarImage !== null) {
        formData.append("avatarImage", avatarImage);
      }

      if (cardMedia !== null) {
        formData.append("cardMedia", cardMedia);
      }
      formData.append("id", dataForm[0].id);

      const response = await doPost("/api/collection/edit/media/save",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

      if (response.data.status === 'success') {
        setSucMessage(response.data.message);
        setIsSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Reset form fields
        setBannerImage(null);
        setAvatarImage(null);
        setCardMedia(null);
        setPreviewType(null);

        // Increment reset key to trigger re-render of upload components
        setResetKey(prevKey => prevKey + 1);

      } else {
        setErMessage(response.data.message);
      }
    } catch (error: any) {
      console.error(error);
      if (error.response) {
        const errorMessage: string = error.response.data.message || "An error occurred while updating the collection media.";
        setErMessage(errorMessage);
      } else {
        setErMessage("An unexpected error occurred. Please try again.");
      }
    }
  };

  const heading = `Edit ${loading ? null : data[0]?.name} Details`
  const subtitle = `Customise the look and feel of your collection ${loading ? null : data[0]?.name}. Images and links are allowed in the details section at the bottom.`

  const handleCustomTagsChange = (newTags: string[]) => {
    setCustomTags(newTags);
    console.log(customTags);
  };

  const handleLicenseChange = (value: string | null, option: ComboboxItem) => {
    setLicense(option.value);
    console.log(license);
  };

  // Nice loading 
  if (loading) {
    return <LoadingSpinner />;
  } else {
    return (
      <>
        <Helmet>
          <title>Cardano Art - Edit Collection</title>
        </Helmet>
        <div
          className={`nc-HeadBackgroundCommon h-24 2xl:h-28 mt-12 left-0 right-0 w-full bg-primary-50 dark:bg-neutral-800/20 `}
          data-nc-id="HeadBackgroundCommon"
        >
          <GeomBackground type="grid_small" heading={heading} subtitle={subtitle} />
        </div>
        <div className="w-full min-h-screen mt-16">
          <div className="container max-w-8xl">
            <Tabs value={activeTab} onChange={setActiveTab} classNames={{ tab: "dark:hover:bg-gray-500 dark:hover:text-white" }}>
              <Tabs.List>
                <Tabs.Tab value="details">Edit Details</Tabs.Tab>
                <Tabs.Tab value="images">Change Images</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="details">
                <div className="mt-6">
                  <FormItem label="Name" className="my-3 max-w-xl">
                    <Input
                      type="text"
                      name="name"
                      value={dataForm[0]?.name || ""}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setDataForm([{ ...dataForm[0], name: newName }]);
                      }}
                    />
                  </FormItem>
                  <FormItem label="Description">
                    <Textarea
                      name="description"
                      value={dataForm[0]?.description || ""}
                      onChange={(e) => {
                        const newDescription = e.target.value;
                        setDataForm([{ ...dataForm[0], description: newDescription }]);
                      }}
                      rows={6}
                      className="mt-1.5"
                      placeholder="..."
                    />
                  </FormItem>
                  <div className="flex flex-wrap my-4">
                    <FormItem className="w-full pr-3 lg:w-2/3" label="Select Categories">
                      <SelectTags value={selectedTags} onChange={handleTagsChange} />
                    </FormItem>
                    <FormItem label="License" className="w-full lg:w-1/3">
                      <div className="flex">
                        <Select
                          data={["Undisclosed License", "MIT License", "GNU AGPLv3", "GNU GPLv3", "GNU LGPLv3", "Mozilla Public License 2.0", "Apache License 2.0", "Boost Software License 1.0", "The Unlicense"]}
                          placeholder="Undisclosed License"
                          value={license}
                          onChange={handleLicenseChange}
                          className="mt-0.5"
                        />
                      </div>
                    </FormItem>
                  </div>
                  <FormItem label="Custom Tags" className="my-4">
                    <TagsInput
                      value={customTags}
                      onChange={handleCustomTagsChange}
                      placeholder="Add tag"
                      clearable
                    />
                    <p className="text-xs mt-1">Seperate tags by comma or press "Enter"</p>
                  </FormItem>
                  <div className="flex flex-wrap">
                    <FormItem label="Website Link" className="w-full my-3 pr-3 lg:w-1/3">
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                          https://
                        </span>
                        <Input
                          type="url"
                          name="websitepath"
                          value={dataForm[0]?.websitepath || ""}
                          onChange={(e) => {
                            const newWebsitepath = e.target.value;
                            setDataForm([{ ...dataForm[0], websitepath: newWebsitepath }]);
                          }}
                          className="!rounded-l-none"
                          placeholder="website.com/"
                        />
                      </div>
                    </FormItem>
                    <FormItem label="Twitter Link" className="w-full my-3 lg:px-3 lg:w-1/3">
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                          https://
                        </span>
                        <Input
                          type="text"
                          name="twitterpath"
                          value={dataForm[0]?.twitterpath || ""}
                          onChange={(e) => {
                            const newTwitterpath = e.target.value;
                            setDataForm([{ ...dataForm[0], twitterpath: newTwitterpath }]);
                          }}
                          className="!rounded-l-none"
                          placeholder="twitter.com/"
                        />
                      </div>
                    </FormItem>
                    <FormItem label="Discord Link" className="w-full my-3 lg:px-3 lg:w-1/3">
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                          https://
                        </span>
                        <Input
                          type="text"
                          name="discordpath"
                          value={dataForm[0]?.discordpath || ""}
                          onChange={(e) => {
                            const newDiscordpath = e.target.value;
                            setDataForm([{ ...dataForm[0], discordpath: newDiscordpath }]);
                          }}
                          className="!rounded-l-none"
                          placeholder="discord.com/"
                        />
                      </div>
                    </FormItem>
                  </div>
                  <div className="flex flex-wrap">
                    <FormItem label="Artist Link" className="w-full my-3 pr-3 lg:w-1/3">
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                          https://
                        </span>
                        <Input
                          type="url"
                          name="artistpath"
                          value={dataForm[0]?.artistpath || ""}
                          onChange={(e) => {
                            const newArtistpath = e.target.value;
                            setDataForm([{ ...dataForm[0], artistpath: newArtistpath }]);
                          }}
                          className="!rounded-l-none"
                          placeholder="website.com/"
                        />
                      </div>
                    </FormItem>
                    <FormItem label="Language" className="w-full my-3 lg:px-3 lg:w-1/3">
                      <div className="flex">
                        <Input
                          type="text"
                          name="language"
                          value={dataForm[0]?.language || ""}
                          onChange={(e) => {
                            const newLanguage = e.target.value;
                            setDataForm([{ ...dataForm[0], language: newLanguage }]);
                          }}
                          className=""
                          placeholder="Javascript"
                        />
                      </div>
                    </FormItem>
                    <FormItem label="Library" className="w-full my-3 lg:px-3 lg:w-1/3">
                      <div className="flex ">
                        <Input
                          type="text"
                          name="library"
                          value={dataForm[0]?.library || ""}
                          onChange={(e) => {
                            const newLibrary = e.target.value;
                            setDataForm([{ ...dataForm[0], library: newLibrary }]);
                          }}
                          className=""
                          placeholder="3.js"
                        />
                      </div>
                    </FormItem>
                  </div>
                  <div className="flex flex-wrap my-4 gap-4">
                    <FormItem label="Display Notes" className="w-full lg:w-6/12">
                      <Textarea
                        name="notes"
                        value={dataForm[0]?.notes || ""}
                        onChange={(e) => {
                          const newNotes = e.target.value;
                          setDataForm([{ ...dataForm[0], notes: newNotes }]);
                        }}
                        rows={6}
                        className="mt-1.5"
                        placeholder="..."
                      />
                    </FormItem>
                    <FormItem label="Artist Credits" className="w-full lg:w-5/12">
                      <Textarea
                        name="credits"
                        value={dataForm[0]?.credits || ""}
                        onChange={(e) => {
                          const newCredits = e.target.value;
                          setDataForm([{ ...dataForm[0], credits: newCredits }]);
                        }}
                        rows={6}
                        className="mt-1.5"
                        placeholder="..."
                      />
                    </FormItem>
                  </div>
                  <FormItem label="Details" className="my-4"></FormItem>
                  <div className="">
                    <RichTextEditor editor={editor} onChange={handleEditorChange} >
                      <RichTextEditor.Toolbar sticky stickyOffset={60} className="text-black">
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                          <RichTextEditor.Strikethrough />
                          <RichTextEditor.ClearFormatting />
                          <RichTextEditor.Highlight />
                          <RichTextEditor.Code />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.H1 />
                          <RichTextEditor.H2 />
                          <RichTextEditor.H3 />
                          <RichTextEditor.H4 />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Blockquote />
                          <RichTextEditor.Hr />
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                          <RichTextEditor.Subscript />
                          <RichTextEditor.Superscript />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Control
                            onClick={addImage}
                            aria-label="Insert star emoji"
                            title="Insert star emoji"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-4 h-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                              />
                            </svg>
                          </RichTextEditor.Control>
                          <RichTextEditor.Link />
                          <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.AlignLeft />
                          <RichTextEditor.AlignCenter />
                          <RichTextEditor.AlignJustify />
                          <RichTextEditor.AlignRight />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>
                      <RichTextEditor.Content />
                    </RichTextEditor>
                  </div>
                  <div className="flex justify-center my-12">
                    <ButtonPrimary className="mb-12" onClick={onSave}>Save Details</ButtonPrimary>
                  </div>
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="images">
                <div className="mt-6">
                  <form
                    onSubmit={handleFormSubmit}
                    className="my-2"
                    encType="multipart/form-data"
                  >
                    <div className="md:grid-cols-3 grid-cols-1 grid gap-4  mb-6">
                      <div className="w-full">
                        <FormItem
                          label="Card Image/Video"
                          desc="Images and Videos allowed for your collection card"
                        >
                          <MediaUpload
                            name="cardMedia"
                            onFileSelect={(file, type) => handleCardMediaSelect(file)}
                            key={`card-${resetKey}`}
                          />
                        </FormItem>
                      </div>

                      <div className="w-full">
                        <FormItem
                          label="Banner Image"
                          desc="Image files only, GIFs are allowed. Ideal size 1920 x 500"
                        >
                          <ImageUpload
                            name="avatarImage"
                            onFileSelect={(file) => handleAvatarImageSelect(file)}
                            key={`avatar-${resetKey}`}
                          />
                        </FormItem>
                      </div>

                      <div className="w-full">
                        <FormItem
                          label="Avatar Image"
                          desc="Image files only, GIFs are allowed. Ideal size 500 x 500"
                        >
                          <ImageUpload
                            name="bannerImage"
                            onFileSelect={(file) => handleBannerImageSelect(file)}
                            key={`banner-${resetKey}`}
                          />
                        </FormItem>
                      </div>
                    </div>
                    <div className="flex justify-center my-12">
                      <ButtonPrimary className="" type="submit">
                        Update Images
                      </ButtonPrimary>
                    </div>
                  </form>
                </div>
              </Tabs.Panel>
            </Tabs>

          </div>
          <div className="flex justify-center mt-12">
            {sucMessage && (
              <div className="fixed bottom-4 right-4 z-10">
                <Toast className="bg-green-500">
                  <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200">
                    <BadgeCheckIcon className="h-5 w-5" />
                  </div>
                  <div className="ml-3 text-sm font-normal text-white px-3">
                    Content has been saved successfully.
                  </div>
                  <Toast.Toggle />
                </Toast>
              </div>
            )}
            {erMessage && (
              <div className="fixed bottom-4 right-4 z-10">
                <Toast className="bg-red-500">
                  <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-500 dark:bg-green-800 dark:text-red-200">
                    <ExclamationCircleIcon className="h-5 w-5" />
                  </div>
                  <div className="ml-3 text-sm font-normal text-white px-3">
                    {erMessage}
                  </div>
                  <Toast.Toggle onClick={() => setErMessage("")} />
                </Toast>
              </div>
            )}

          </div>
        </div>
      </>
    );
  }
};

export default PageCollectionEdit;
